import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { projectsData } from '../../config/constants/projectData';
import { findProjectBySlug, getProjectUrl } from '../../utils/slugify';
import SEO from '../../components/common/SEO';
import Breadcrumb from '../../components/common/Breadcrumb';
import ProjectCard from '../../components/common/ProjectCard';

const deliverablesFor = (category) =>
    category === "Branding" || category === "Event Branding"
        ? "Logo Design, Brand Guidelines, Packaging, Stationery"
        : "Wireframes, UI Design, Prototype, Design System";

const fallbackImage = (width, height, label) =>
    `data:image/svg+xml,${encodeURIComponent(`
        <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
            <rect width="${width}" height="${height}" fill="#f3f4f6"/>
            <text x="${width / 2}" y="${height / 2}" text-anchor="middle" font-family="Montserrat, sans-serif" font-size="20" fill="#6b7280">${label}</text>
        </svg>
    `)}`;

const ProjectDetails = () => {
    const { slug } = useParams();
    const [project, setProject] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeImage, setActiveImage] = useState(0);

    useEffect(() => {
        setIsLoading(true);
        setActiveImage(0);
        setTimeout(() => {
            const foundProject = findProjectBySlug(projectsData, slug);
            setProject(foundProject);
            setIsLoading(false);
        }, 800);
    }, [slug]);

    const handlePrevImage = useCallback(() => {
        if (project && project.gallery) {
            setActiveImage((prev) => (prev === 0 ? project.gallery.length - 1 : prev - 1));
        }
    }, [project]);

    const handleNextImage = useCallback(() => {
        if (project && project.gallery) {
            setActiveImage((prev) => (prev === project.gallery.length - 1 ? 0 : prev + 1));
        }
    }, [project]);

    useEffect(() => {
        const handleKeyPress = (e) => {
            if (project && project.gallery && project.gallery.length > 0) {
                if (e.key === 'ArrowLeft') {
                    handlePrevImage();
                } else if (e.key === 'ArrowRight') {
                    handleNextImage();
                }
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [project, handlePrevImage, handleNextImage]);

    // Get related projects (same category)
    const relatedProjects = project ? projectsData
        .filter(p => p.category === project.category && p.slug !== project.slug)
        .slice(0, 3) : [];

    // Previous / next project (wraps around the archive)
    const currentIndex = project ? projectsData.findIndex(p => p.id === project.id) : -1;
    const prevProject = currentIndex >= 0
        ? projectsData[(currentIndex - 1 + projectsData.length) % projectsData.length]
        : null;
    const nextProject = currentIndex >= 0
        ? projectsData[(currentIndex + 1) % projectsData.length]
        : null;

    // Case-study chapters rendered from the project record
    const chapters = project ? [
        { number: "01", title: "The Challenge", body: project.challenge },
        { number: "02", title: "The Solution", body: project.solution },
        { number: "03", title: "The Results", body: project.results },
    ].filter(chapter => chapter.body) : [];

    // Generate structured data for SEO
    const generateStructuredData = () => {
        if (!project) return null;
        
        return {
            "@context": "https://schema.org",
            "@type": "CreativeWork",
            "name": project.title,
            "description": project.description,
            "creator": {
                "@type": "Organization",
                "name": "Arvix Creative"
            },
            "dateCreated": project.date,
            "genre": project.category,
            "image": project.image.startsWith("http") ? project.image : `https://arvixcreative.com${project.image}`,
            "url": `https://arvixcreative.com/projects/${project.slug}`,
            "client": {
                "@type": "Organization",
                "name": project.client
            },
            "keywords": [project.category, "design", "creative", "branding", "UI/UX"].join(", ")
        };
    };

    if (isLoading) {
        return (
            <>
                <SEO 
                    title="Loading Project..."
                    description="Loading project details..."
                />
                <div className="min-h-screen pt-24 pb-20 bg-white flex flex-col justify-center items-center gap-6">
                    <p className="font-mono text-[11px] font-bold tracking-[0.22em] uppercase text-gray-400">Opening case study</p>
                    <div className="w-12 h-12 border-4 border-designer-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
            </>
        );
    }

    if (!project) {
        return (
            <>
                <SEO 
                    title="Project Not Found | Arvix Creative"
                    description="The project you are looking for does not exist or has been removed."
                />
                <div className="min-h-screen pt-24 pb-20 bg-white">
                    <div className="w-full text-center">
                        <p className="font-mono text-[11px] font-bold tracking-[0.22em] uppercase text-designer-primary">Missing entry</p>
                        <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-designer-dark text-balance">Project not found</h1>
                        <p className="mt-4 text-gray-600">The project you are looking for does not exist or has been removed.</p>
                        <Link 
                            to="/projects" 
                            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-designer-dark text-white text-sm font-semibold hover:bg-designer-primary transition-colors"
                        >
                            <FaArrowLeft className="w-3 h-3" aria-hidden="true" /> Back to projects
                        </Link>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <SEO 
                title={`${project.title} | ${project.category} Project | Arvix Creative`}
                description={project.description}
                keywords={`${project.title}, ${project.category}, design, creative, branding, UI/UX, ${project.client}, Arvix Creative`}
                image={project.image}
                url={`https://arvixcreative.com/projects/${project.slug}`}
                type="article"
                structuredData={generateStructuredData()}
            />
            <article className="min-h-screen pt-24 pb-20 relative overflow-hidden bg-white">
            
            <div className="w-full relative z-10">
                <Breadcrumb
                    items={[{ label: "Projects", to: "/projects" }, { label: project.title }]}
                    className="mb-10 md:mb-14"
                />

                {/* Article header */}
                <header className="animate-slide-down">
                    <div className="flex items-center gap-4 font-mono text-[11px] font-bold tracking-[0.22em] uppercase text-designer-dark/60">
                        <span className="text-designer-primary">Case study</span>
                        <span className="h-px w-10 bg-designer-dark/20" aria-hidden="true" />
                        <span>{project.category}</span>
                    </div>
                    <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-designer-dark text-balance max-w-4xl">
                        {project.title}
                    </h1>
                    <p className="mt-6 max-w-2xl text-lg md:text-xl leading-relaxed text-gray-600">
                        {project.description}
                    </p>
                    <dl className="mt-8 grid grid-cols-2 md:grid-cols-4 border-y border-designer-dark/15 divide-x divide-designer-dark/15">
                        {[
                            { term: "Client", value: project.client },
                            { term: "Discipline", value: project.category },
                            { term: "Delivered", value: project.date },
                            { term: "Studio", value: "Arvix Creative" },
                        ].map((fact) => (
                            <div key={fact.term} className="px-4 md:px-6 py-4">
                                <dt className="font-mono text-[10px] font-bold tracking-[0.22em] uppercase text-gray-400">{fact.term}</dt>
                                <dd className="mt-1.5 text-sm font-semibold text-designer-dark leading-snug">{fact.value}</dd>
                            </div>
                        ))}
                    </dl>
                </header>

                {/* Hero figure */}
                <figure className="mt-10 md:mt-14 animate-fade-in">
                    <div className="overflow-hidden rounded-3xl border border-designer-dark/10">
                        <img 
                            src={project.image} 
                            alt={project.title} 
                            className="w-full h-auto"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = fallbackImage(1200, 600, "Project Image");
                            }}
                        />
                    </div>
                    <figcaption className="mt-3 flex items-baseline justify-between gap-4 font-mono text-[11px] text-gray-400">
                        <span>Fig. 01: {project.title}</span>
                        <span className="hidden sm:inline">{project.client}</span>
                    </figcaption>
                </figure>

                {/* Body + project file */}
                <div className="mt-12 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
                    <div className="lg:col-span-8 animate-slide-up">
                        <p className="font-mono text-[11px] font-bold tracking-[0.22em] uppercase text-designer-primary">Overview</p>
                        <p className="mt-4 text-lg leading-[1.85] text-designer-dark/85 first-letter:font-display first-letter:text-6xl first-letter:font-bold first-letter:float-left first-letter:mr-3 first-letter:leading-[0.85] first-letter:text-designer-dark">
                            {project.description}
                        </p>

                        {chapters.map((chapter) => (
                            <section key={chapter.number} className="mt-12 border-t border-designer-dark/15 pt-8">
                                <div className="flex items-baseline gap-4">
                                    <span className="font-mono text-sm font-bold text-designer-primary" aria-hidden="true">{chapter.number}</span>
                                    <h2 className="font-display text-2xl md:text-3xl font-bold text-designer-dark">{chapter.title}</h2>
                                </div>
                                <p className="mt-4 leading-[1.85] text-gray-600">{chapter.body}</p>
                            </section>
                        ))}
                    </div>
                    
                    <aside className="lg:col-span-4 animate-fade-in">
                        <div className="lg:sticky lg:top-28 border border-designer-dark/10 rounded-3xl p-7 md:p-8 bg-white">
                            <h2 className="font-mono text-[11px] font-bold tracking-[0.22em] uppercase text-gray-400">Project file</h2>
                            <dl className="mt-5 space-y-5">
                                <div>
                                    <dt className="text-xs font-semibold text-gray-400">Client</dt>
                                    <dd className="mt-1 font-semibold text-designer-dark">{project.client}</dd>
                                </div>
                                <div>
                                    <dt className="text-xs font-semibold text-gray-400">Discipline</dt>
                                    <dd className="mt-1 font-semibold text-designer-dark">{project.category}</dd>
                                </div>
                                <div>
                                    <dt className="text-xs font-semibold text-gray-400">Delivered</dt>
                                    <dd className="mt-1 font-semibold text-designer-dark">{project.date}</dd>
                                </div>
                                <div>
                                    <dt className="text-xs font-semibold text-gray-400">Deliverables</dt>
                                    <dd className="mt-1 text-[15px] leading-relaxed text-gray-600">{deliverablesFor(project.category)}</dd>
                                </div>
                            </dl>
                            <div className="mt-7 border-t border-designer-dark/10 pt-6">
                                <p className="font-display text-xl font-bold text-designer-dark leading-snug">Have something similar in mind?</p>
                                <Link
                                    to="/contact"
                                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-designer-dark px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-designer-primary"
                                >
                                    Start a project
                                    <FaArrowRight className="w-3 h-3" aria-hidden="true" />
                                </Link>
                            </div>
                        </div>
                    </aside>
                </div>

                {/* Project Gallery */}
                {project.gallery && project.gallery.length > 0 && (
                    <section className="mt-16 md:mt-24 animate-fade-in" aria-label="Project gallery">
                        <div className="flex items-end justify-between gap-4 border-b border-designer-dark/15 pb-5">
                            <div>
                                <p className="font-mono text-[11px] font-bold tracking-[0.22em] uppercase text-designer-primary">Plates {String(activeImage + 1).padStart(2, "0")} to {String(project.gallery.length).padStart(2, "0")}</p>
                                <h2 className="mt-2 font-display text-2xl md:text-3xl font-bold text-designer-dark">Gallery</h2>
                            </div>
                            {project.gallery.length > 1 && (
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={handlePrevImage}
                                        className="p-3 rounded-full border border-designer-dark/15 text-designer-dark transition-colors hover:bg-designer-dark hover:text-white hover:border-designer-dark"
                                        aria-label="Previous image"
                                    >
                                        <FaChevronLeft />
                                    </button>
                                    <button
                                        onClick={handleNextImage}
                                        className="p-3 rounded-full border border-designer-dark/15 text-designer-dark transition-colors hover:bg-designer-dark hover:text-white hover:border-designer-dark"
                                        aria-label="Next image"
                                    >
                                        <FaChevronRight />
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Main Gallery Image */}
                        <div className="mt-8 relative">
                            <div className="overflow-hidden rounded-3xl border border-designer-dark/10">
                                <img
                                    src={project.gallery[activeImage]}
                                    alt={`${project.title} gallery ${activeImage + 1}`}
                                    className="w-full h-auto"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = fallbackImage(1200, 600, "Gallery Image");
                                    }}
                                />
                            </div>

                            {/* Image Counter */}
                            {project.gallery.length > 1 && (
                                <div className="absolute bottom-4 right-4 bg-designer-dark/85 text-white px-3.5 py-1.5 rounded-full font-mono text-xs font-bold">
                                    {activeImage + 1} / {project.gallery.length}
                                </div>
                            )}
                        </div>

                        {/* Thumbnails */}
                        <div className="mt-5 grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                            {project.gallery.map((image, index) => (
                                <button
                                    key={index}
                                    onClick={() => setActiveImage(index)}
                                    aria-label={`Show gallery image ${index + 1}`}
                                    aria-pressed={activeImage === index}
                                    className={`rounded-2xl overflow-hidden border transition-all ${activeImage === index ? 'border-designer-primary ring-2 ring-designer-primary ring-offset-2' : 'border-designer-dark/10 opacity-70 hover:opacity-100 hover:border-gray-300'}`}
                                >
                                    <img
                                        src={image}
                                        alt={`${project.title} thumbnail ${index + 1}`}
                                        className="w-full h-auto aspect-video object-cover"
                                        loading="lazy"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = fallbackImage(300, 200, "Thumbnail");
                                        }}
                                    />
                                </button>
                            ))}
                        </div>
                    </section>
                )}

                {/* Previous / Next */}
                {prevProject && nextProject && (
                    <nav className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-2 border-y border-designer-dark/15 divide-y md:divide-y-0 md:divide-x divide-designer-dark/15 animate-slide-up" aria-label="More projects">
                        <Link 
                            to={getProjectUrl(prevProject.slug)}
                            className="group px-2 md:px-8 py-8 md:py-10 flex items-center gap-5"
                        >
                            <FaArrowLeft className="shrink-0 text-gray-300 transition-all group-hover:text-designer-primary group-hover:-translate-x-1" aria-hidden="true" />
                            <span>
                                <span className="block font-mono text-[11px] font-bold tracking-[0.22em] uppercase text-gray-400">Previous entry</span>
                                <span className="mt-2 block font-display text-xl md:text-2xl font-bold text-designer-dark leading-snug group-hover:text-designer-primary transition-colors">
                                    {prevProject.title}
                                </span>
                            </span>
                        </Link>
                        
                        <Link 
                            to={getProjectUrl(nextProject.slug)}
                            className="group px-2 md:px-8 py-8 md:py-10 flex items-center justify-end gap-5 text-right"
                        >
                            <span>
                                <span className="block font-mono text-[11px] font-bold tracking-[0.22em] uppercase text-gray-400">Next entry</span>
                                <span className="mt-2 block font-display text-xl md:text-2xl font-bold text-designer-dark leading-snug group-hover:text-designer-primary transition-colors">
                                    {nextProject.title}
                                </span>
                            </span>
                            <FaArrowRight className="shrink-0 text-gray-300 transition-all group-hover:text-designer-primary group-hover:translate-x-1" aria-hidden="true" />
                        </Link>
                    </nav>
                )}

                {/* Related Projects */}
                {relatedProjects.length > 0 && (
                    <section className="mt-16 md:mt-24 animate-fade-in" aria-label="Related projects">
                        <div className="flex items-end justify-between gap-4 mb-8">
                            <div>
                                <p className="font-mono text-[11px] font-bold tracking-[0.22em] uppercase text-designer-primary">Keep reading</p>
                                <h2 className="mt-2 font-display text-2xl md:text-3xl font-bold text-designer-dark">More in {project.category}</h2>
                            </div>
                            <Link
                                to="/projects"
                                className="hidden sm:inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-designer-dark hover:text-designer-primary transition-colors"
                            >
                                <span className="link-sweep">Full archive</span>
                                <FaArrowRight className="w-3 h-3" aria-hidden="true" />
                            </Link>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
                            {relatedProjects.map((relatedProject, index) => (
                                <ProjectCard key={relatedProject.id} project={relatedProject} index={index} />
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </article>
        </>
    );
};

export default ProjectDetails;
